// src/components/Layout.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { 
  Home, Clock, Calendar, Users, MessageSquare, CalendarCheck, Settings, LogOut, 
  Menu, X, Bell, User, ChevronDown, FileText, Briefcase, Building, Monitor
} from 'lucide-react';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showITPanel, setShowITPanel] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const profileRef = useRef(null);
  const bellRef = useRef(null);
  const itRef = useRef(null);

  const roleId = parseInt(localStorage.getItem('role_id')) || 0;
  const isDeveloper = roleId === 1;
  const isAdmin = roleId === 2;
  const isEmployee = roleId === 3;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home size={20} />, roles: ['employee', 'admin', 'developer'] },
    { 
      label: 'Timesheet', 
      icon: <Clock size={20} />,
      children: [
        { path: '/timesheets', label: 'Add Sheet', icon: <FileText size={18} /> },
        { path: '/calendar', label: 'Calendar', icon: <Calendar size={20} /> },
        { path: '/messages', label: 'Query', icon: <MessageSquare size={18} /> },
        { path: '/Reporting', label: 'Reports', icon: <Users size={20} /> },
      ],
      roles: ['employee', 'admin', 'developer']
    },
    { path: '/workstream', label: 'Workstream', icon: <Briefcase size={20} />, roles: ['employee', 'admin', 'developer'] },
    { path: '/asset-manager', label: 'Asset Manager', icon: <Building size={20} />, roles: ['admin', 'developer'] },
    { path: '/leave', label: 'Leave', icon: <CalendarCheck size={20} />, roles: ['employee', 'admin', 'developer'] },
    { 
      label: 'Departments', 
      icon: <Users size={20} />,
      children: [
        { path: '/departments/marketing', label: 'Marketing' },
        { path: '/departments/it', label: 'IT Department' },
        { path: '/departments/hr', label: 'HR Department' },
      ],
      roles: ['admin', 'developer']
    },
    { 
      label: 'Settings', 
      icon: <Settings size={20} />,
      children: [
        { path: '/settings', label: 'General Settings' },
        { path: '/users', label: 'Users', icon: <Users size={20} /> },
      ],
      roles: ['admin', 'developer']
    },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (isDeveloper) return true;
    if (isAdmin) return item.roles.includes('admin');
    return item.roles.includes('employee');
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const userRes = await api.get('/users/me', { headers: { Authorization: `Bearer ${token}` } });
        setUser(userRes.data);

        const notifRes = await api.get('/notifications', { headers: { Authorization: `Bearer ${token}` } });
        const notifs = notifRes.data || [];
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.is_read).length);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) setShowProfileMenu(false);
      if (bellRef.current && !bellRef.current.contains(event.target)) setShowNotifications(false);
      if (itRef.current && !itRef.current.contains(event.target)) setShowITPanel(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const avatarUrl = user?.avatar_url ? `/${user.avatar_url}` : null;
  const firstName = user?.first_name || user?.username || 'User';

  const Breadcrumb = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    return (
      <nav className="flex mb-6 text-sm text-gray-500">
        <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
        {segments.map((segment, i) => {
          const isLast = i === segments.length - 1;
          const to = '/' + segments.slice(0, i + 1).join('/');
          return (
            <span key={i} className="flex items-center">
              <span className="mx-2">›</span>
              {isLast ? (
                <span className="text-gray-900 font-medium capitalize">{segment.replace('-', ' ')}</span>
              ) : (
                <Link to={to} className="hover:text-gray-700 capitalize">{segment.replace('-', ' ')}</Link>
              )}
            </span>
          );
        })}
      </nav>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-custom-orange border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      {/* HEADER */}
      <header className="bg-[#0e141b] text-white p-4 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-4">
          <img src="/logo-w.png" alt="Logo" className="h-10" />
        </div>

        <div className="flex items-center gap-6">
          {/* Notifications */}
          <div className="relative" ref={bellRef}>
            <button onClick={() => setShowNotifications(!showNotifications)} className="p-2 text-white hover:text-custom-orange relative">
              <Bell size={24} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 px-2 py-1 text-xs font-bold bg-red-600 rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[80vh] overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                  <button onClick={() => setShowNotifications(false)}>
                    <X size={20} className="text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-12 text-center">
                      <Bell size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map(notif => (
                      <div key={notif.id} className={`p-4 border-b hover:bg-gray-50 ${!notif.is_read ? 'bg-blue-50' : ''}`}>
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-gray-900">{notif.title}</h4>
                          <span className="text-xs text-gray-500">
                            {new Date(notif.created_at).toLocaleString('en-ZA')}
                          </span>
                        </div>
                        <p className="mt-1 text-gray-700">{notif.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile - Greeting Left, Avatar Right */}
          <div className="relative" ref={profileRef}>
            <button onClick={() => setShowProfileMenu(!showProfileMenu)} className="flex items-center gap-3 focus:outline-none hover:bg-white/10 px-3 py-1.5 rounded-lg transition">
              <div className="hidden md:block text-left">
                <p className="text-xs text-white/70">Hi there,</p>
                <p className="font-medium text-white text-sm -mt-0.5">{firstName}</p>
              </div>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="w-9 h-9 rounded-full object-cover border-2 border-custom-orange" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-custom-orange flex items-center justify-center text-white font-bold border-2 border-custom-orange text-sm">
                  {firstName[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-50 py-2">
                <button onClick={() => { setShowProfileMenu(false); navigate('/profile'); }} className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-800">
                  <User size={18} /> Profile
                </button>
                <button onClick={() => { setShowProfileMenu(false); navigate('/messages'); }} className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-800 relative">
                  <Bell size={18} /> Notifications
                  {unreadCount > 0 && <span className="absolute right-4 bg-red-600 text-white text-xs px-2 py-1 rounded-full">{unreadCount}</span>}
                </button>
                <hr className="my-2 border-gray-200" />
                <button onClick={() => { setShowProfileMenu(false); handleLogout(); }} className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 flex items-center gap-3">
                  <LogOut size={18} /> Log out
                </button>
              </div>
            )}
          </div>

          <button className="md:hidden p-2 text-white hover:text-custom-orange" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR */}
        <aside className={`bg-gradient-to-b from-gray-100 to-gray-200 text-gray-800 w-64 flex-shrink-0 flex flex-col min-h-screen fixed md:relative z-50 transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
          <div className="md:hidden p-4 flex justify-between items-center border-b border-gray-300">
            <h2 className="text-lg font-bold">Management App</h2>
            <button onClick={() => setIsMobileMenuOpen(false)}><X size={24} /></button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-bold p-6 text-center hidden md:block">Management App</h2>
            
            <nav className="px-4 pb-6">
              <ul className="space-y-1">
                {filteredNavItems.map((item, index) => {
                  const isActive = item.path ? location.pathname === item.path : false;
                  const isChildActive = item.children?.some(child => child.path && location.pathname === child.path);
                  const isExpanded = expandedMenus.has(item.label) || isChildActive;

                  const toggleExpand = () => {
                    setExpandedMenus(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(item.label)) newSet.delete(item.label);
                      else newSet.add(item.label);
                      return newSet;
                    });
                  };

                  return (
                    <li key={index}>
                      {item.children ? (
                        <div>
                          <div onClick={toggleExpand} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer hover:bg-[#949eab] text-gray-700 ${isChildActive ? 'bg-gray-300 font-medium' : ''}`}>
                            <div className="flex items-center gap-3">
                              {item.icon}
                              <span className="font-medium">{item.label}</span>
                            </div>
                            <ChevronDown size={18} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>

                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <ul className="ml-8 mt-1 space-y-1 border-l border-gray-300 pl-4">
                              {item.children.map((subItem, subIndex) => {
                                if (subItem.label === 'General Settings' && !isDeveloper) return null;
                                if (subItem.label === 'Reports' && isEmployee) return null;

                                const subIsActive = location.pathname === subItem.path;
                                return (
                                  <li key={subIndex}>
                                    <Link to={subItem.path} onClick={() => setIsMobileMenuOpen(false)} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${subIsActive ? 'bg-custom-orange text-white' : 'hover:bg-[#949eab] text-gray-600'}`}>
                                      {subItem.icon}
                                      <span>{subItem.label}</span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <Link to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all hover:bg-[#949eab] ${isActive ? 'bg-custom-orange text-white shadow-md' : 'text-gray-700'}`}>
                          {item.icon}
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="p-4 border-t border-gray-300">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-custom-orange hover:bg-orange-600 text-white transition-colors">
              <LogOut size={20} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </aside>

        {isMobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />}

        <main className="flex-1 overflow-y-auto bg-gray-50 p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating IT Department */}
      <div className="fixed bottom-6 right-6 z-[70]" ref={itRef}>
        <button
          onClick={() => setShowITPanel(!showITPanel)}
          className="group flex items-center gap-3 bg-[#0e141b] hover:bg-gray-800 text-white pl-4 pr-5 py-3 rounded-full shadow-2xl transition-all duration-200 hover:scale-105"
        >
          <Monitor size={22} />
          <span className="hidden group-hover:inline font-medium text-sm">IT Department</span>
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
        </button>

        {showITPanel && (
          <div className="absolute bottom-20 right-0 bg-white rounded-2xl shadow-2xl p-6 w-80 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Monitor size={24} className="text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">IT Department</h3>
                <p className="text-sm text-gray-500">Technical Support</p>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Need help with technical issues, system access, or software requests?
            </p>

            <button
              onClick={() => {
                setShowITPanel(false);
                navigate('/departments/it');
              }}
              className="w-full bg-custom-orange hover:bg-orange-600 text-white font-medium py-3 rounded-xl transition flex items-center justify-center gap-2"
            >
              Go to IT Department Page
            </button>

            <button onClick={() => setShowITPanel(false)} className="w-full mt-3 text-gray-500 hover:text-gray-700 py-2 text-sm">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;